<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<!--
  AscendQE: Add Employee is a four-step onboarding wizard. Each step is its own
  oxd-form (only the current one is mounted), so "Next" validates just that
  step; the Review step re-checks every step's required fields and links back
  to whichever one is incomplete. Drafts autosave to localStorage.
-->
<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container aqe-wiz">
      <div class="aqe-wiz__top">
        <div>
          <oxd-text tag="h6" class="orangehrm-main-title">
            {{ $t('general.add_employee') }}
          </oxd-text>
          <p class="aqe-wiz__sub">{{ steps[step].hint }}</p>
        </div>
        <span class="aqe-wiz__saved" aria-live="polite">
          <template v-if="savedAt">
            <i class="oxd-icon bi-cloud-check"></i> Draft saved {{ savedAt }}
          </template>
        </span>
      </div>

      <div v-if="draftOffer" class="aqe-wiz__draft" role="status">
        <i class="oxd-icon bi-clock-history"></i>
        <span>
          You have an unfinished employee from {{ draftOffer.when }}
          <strong v-if="draftOffer.name">({{ draftOffer.name }})</strong>.
        </span>
        <button type="button" class="aqe-wiz__link" @click="restoreDraft">
          Restore
        </button>
        <button
          type="button"
          class="aqe-wiz__link is-muted"
          @click="discardDraft"
        >
          Discard
        </button>
      </div>

      <!-- stepper -->
      <ol class="aqe-wiz__steps" role="tablist" aria-label="Onboarding steps">
        <li
          v-for="(s, i) in steps"
          :key="s.key"
          role="presentation"
          :class="{
            'is-current': i === step,
            'is-done': visited[i] && !missing[s.key].length && i !== step,
            'is-error': showIssues && missing[s.key].length,
          }"
        >
          <button
            type="button"
            role="tab"
            :aria-selected="String(i === step)"
            :aria-controls="'wiz-panel-' + s.key"
            :disabled="!visited[i] && i > furthest + 1"
            @click="goTo(i)"
          >
            <span class="aqe-wiz__dot">
              <i
                v-if="visited[i] && !missing[s.key].length && i !== step"
                class="oxd-icon bi-check-lg"
              ></i>
              <i
                v-else-if="showIssues && missing[s.key].length"
                class="oxd-icon bi-exclamation-lg"
              ></i>
              <template v-else>{{ i + 1 }}</template>
            </span>
            <span class="aqe-wiz__stxt">
              <span class="aqe-wiz__sname">{{ s.title }}</span>
              <span class="aqe-wiz__smeta">{{ stepMeta(s, i) }}</span>
            </span>
          </button>
        </li>
      </ol>
      <div class="aqe-wiz__progress" aria-hidden="true">
        <span :style="{width: ((step + 1) / steps.length) * 100 + '%'}"></span>
      </div>

      <!-- ── step 1: basics ── -->
      <oxd-form
        v-if="step === 0"
        id="wiz-panel-basics"
        ref="stepForm"
        role="tabpanel"
        :loading="isLoading"
        @submit-valid="next"
      >
        <div class="orangehrm-employee-container">
          <div class="aqe-photo">
            <div
              class="aqe-photo__drop"
              :class="{'is-over': dragOver, 'has-img': !!employee.empPicture}"
              role="button"
              tabindex="0"
              :aria-label="
                employee.empPicture
                  ? 'Change profile photo'
                  : 'Upload profile photo'
              "
              @click="$refs.photoInput.click()"
              @keydown.enter.prevent="$refs.photoInput.click()"
              @keydown.space.prevent="$refs.photoInput.click()"
              @dragenter.prevent="dragOver = true"
              @dragover.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="onDrop"
            >
              <img
                v-if="employee.empPicture"
                :src="profilePicUrl"
                alt="Profile photo preview"
              />
              <template v-else>
                <i class="oxd-icon bi-cloud-arrow-up"></i>
                <span><strong>Drop a photo</strong> or click to browse</span>
                <small>JPG, PNG or GIF · up to 1 MB</small>
              </template>
              <div v-if="photoProgress !== null" class="aqe-photo__progress">
                <span :style="{width: photoProgress + '%'}"></span>
              </div>
            </div>
            <input
              ref="photoInput"
              class="aqe-photo__input"
              type="file"
              :accept="allowedImageTypes.join(',')"
              @change="onPick"
            />
            <div class="aqe-photo__row">
              <span v-if="employee.empPicture" class="aqe-photo__name">
                {{ employee.empPicture.name }} ·
                {{ kb(employee.empPicture.size) }}
              </span>
              <button
                v-if="employee.empPicture"
                type="button"
                class="aqe-wiz__link is-danger"
                @click="employee.empPicture = null"
              >
                Remove
              </button>
            </div>
            <span v-if="photoError" class="aqe-wiz__err" role="alert">{{
              photoError
            }}</span>
          </div>
          <div class="orangehrm-employee-form">
            <oxd-form-row>
              <oxd-grid :cols="1" class="orangehrm-full-width-grid">
                <oxd-grid-item>
                  <full-name-input
                    v-model:firstName="employee.firstName"
                    v-model:middleName="employee.middleName"
                    v-model:lastName="employee.lastName"
                    :rules="rules"
                  />
                </oxd-grid-item>
              </oxd-grid>
              <oxd-grid :cols="2" class="orangehrm-full-width-grid">
                <oxd-grid-item>
                  <oxd-input-field
                    v-model="employee.employeeId"
                    :label="$t('general.employee_id')"
                    :rules="rules.employeeId"
                  />
                </oxd-grid-item>
              </oxd-grid>
            </oxd-form-row>
          </div>
        </div>
        <wizard-actions
          :step="step"
          :count="steps.length"
          @back="back"
          @cancel="onCancel"
        />
      </oxd-form>

      <!-- ── step 2: personal & contact ── -->
      <oxd-form
        v-if="step === 1"
        id="wiz-panel-personal"
        ref="stepForm"
        role="tabpanel"
        @submit-valid="next"
      >
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="personal.birthday"
                :label="$t('pim.date_of_birth')"
                :rules="rules.birthday"
                :years="birthYears"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="personal.maritalStatus"
                type="select"
                :label="$t('pim.marital_status')"
                :options="maritalOptions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="personal.nationality"
                type="select"
                :label="$t('general.nationality')"
                :options="nationalities"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <div class="aqe-field">
            <span class="oxd-label">{{ $t('pim.gender') }}</span>
            <div
              class="aqe-seg"
              role="radiogroup"
              :aria-label="$t('pim.gender')"
            >
              <button
                v-for="g in genders"
                :key="g.id"
                type="button"
                role="radio"
                :aria-checked="String(personal.gender === g.id)"
                @click="
                  personal.gender = personal.gender === g.id ? null : g.id
                "
              >
                {{ g.label }}
              </button>
            </div>
          </div>
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="contact.workEmail"
                :label="$t('general.work_email')"
                :rules="rules.workEmail"
                placeholder="name@company.com"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                :model-value="contact.mobile"
                :label="$t('general.mobile')"
                :rules="rules.mobile"
                placeholder="+91 98765 43210"
                inputmode="tel"
                @update:model-value="contact.mobile = maskPhone($event)"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="contact.country"
                type="select"
                :label="$t('general.country')"
                :options="countries"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="contact.state"
                type="select"
                :label="$t('general.state_province')"
                :options="states"
                :disabled="!contact.country || geoLoading === 'state'"
                :placeholder="geoLoading === 'state' ? 'Loading…' : undefined"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="contact.city"
                type="select"
                :label="$t('general.city')"
                :options="cities"
                :disabled="!contact.state || geoLoading === 'city'"
                :placeholder="geoLoading === 'city' ? 'Loading…' : undefined"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <wizard-actions
          :step="step"
          :count="steps.length"
          @back="back"
          @cancel="onCancel"
        />
      </oxd-form>

      <!-- ── step 3: job ── -->
      <oxd-form
        v-if="step === 2"
        id="wiz-panel-job"
        ref="stepForm"
        role="tabpanel"
        @submit-valid="next"
      >
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="job.joinedDate"
                :label="$t('general.joined_date')"
                :rules="rules.joinedDate"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <jobtitle-dropdown
                v-model="job.jobTitle"
                :rules="rules.jobTitle"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <employment-status-dropdown
                v-model="job.empStatus"
                :rules="rules.empStatus"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <subunit-dropdown v-model="job.subunit" />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <transition name="aqe-wiz-reveal">
          <div v-if="needsContract" class="aqe-wiz__callout">
            <div class="aqe-wiz__callout-head">
              <i class="oxd-icon bi-file-earmark-text"></i>
              {{ job.empStatus?.label }} employees need a contract period.
            </div>
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <date-input
                  v-model="job.contractStart"
                  :label="$t('pim.contract_start_date')"
                  :rules="rules.contractStart"
                  required
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <date-input
                  v-model="job.contractEnd"
                  :label="$t('pim.contract_end_date')"
                  :rules="rules.contractEnd"
                  required
                />
              </oxd-grid-item>
            </oxd-grid>
          </div>
        </transition>
        <wizard-actions
          :step="step"
          :count="steps.length"
          @back="back"
          @cancel="onCancel"
        />
      </oxd-form>

      <!-- ── step 4: account & review ── -->
      <oxd-form
        v-if="step === 3"
        id="wiz-panel-review"
        ref="stepForm"
        role="tabpanel"
        :loading="isLoading"
        @submit-valid="onSave"
      >
        <oxd-form-row class="user-form-header">
          <oxd-text class="user-form-header-text" tag="p">
            {{ $t('pim.create_login_details') }}
          </oxd-text>
          <oxd-switch-input v-model="createLogin" />
        </oxd-form-row>
        <template v-if="createLogin">
          <oxd-form-row>
            <oxd-grid :cols="2" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="user.username"
                  :label="$t('general.username')"
                  :rules="rules.username"
                  required
                  autocomplete="off"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-group
                  :label="$t('general.status')"
                  :classes="{wrapper: '--status-grouped-field'}"
                >
                  <oxd-input-field
                    v-model="user.status"
                    type="radio"
                    :option-label="$t('general.enabled')"
                    value="1"
                  />
                  <oxd-input-field
                    v-model="user.status"
                    type="radio"
                    :option-label="$t('general.disabled')"
                    value="2"
                  />
                </oxd-input-group>
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>
          <password-input
            v-model:password="user.password"
            v-model:passwordConfirm="user.passwordConfirm"
            :is-password-required="isPasswordRequired"
          />
        </template>

        <oxd-divider />
        <div class="aqe-review">
          <section
            v-for="(s, i) in steps.slice(0, 3)"
            :key="s.key"
            class="aqe-review__card"
          >
            <header>
              <span>{{ s.title }}</span>
              <button type="button" class="aqe-wiz__link" @click="goTo(i)">
                Edit
              </button>
            </header>
            <dl>
              <div v-for="f in summary[s.key]" :key="f.label">
                <dt>{{ f.label }}</dt>
                <dd :class="{'is-missing': f.missing}">
                  {{ f.value || (f.missing ? 'Required' : '—') }}
                </dd>
              </div>
            </dl>
            <p
              v-if="missing[s.key].length"
              class="aqe-review__warn"
              role="alert"
            >
              <i class="oxd-icon bi-exclamation-circle"></i>
              {{ missing[s.key].length }} required
              {{ missing[s.key].length === 1 ? 'field is' : 'fields are' }}
              missing.
              <button type="button" class="aqe-wiz__link" @click="goTo(i)">
                Fix now
              </button>
            </p>
          </section>
        </div>

        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <oxd-button display-type="ghost" label="Back" @click="back" />
          <submit-button :label="$t('general.save')" />
        </oxd-form-actions>
      </oxd-form>
    </div>

    <confirmation-dialog
      ref="leaveDialog"
      title="Discard this employee?"
      subtitle="You have unsaved changes. Your draft stays available on this device."
      cancel-label="Keep editing"
      confirm-label="Leave"
      confirm-button-type="label-danger"
    ></confirmation-dialog>
  </div>
</template>

<script>
import {ref} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import {navigate} from '@ohrm/core/util/helper/navigation';
import FullNameInput from '@/orangehrmPimPlugin/components/FullNameInput';
import PasswordInput from '@/core/components/inputs/PasswordInput';
import JobtitleDropdown from '@/orangehrmPimPlugin/components/JobtitleDropdown';
import SubunitDropdown from '@/orangehrmPimPlugin/components/SubunitDropdown';
import EmploymentStatusDropdown from '@/orangehrmPimPlugin/components/EmploymentStatusDropdown';
import ConfirmationDialog from '@ohrm/components/dialogs/ConfirmationDialog';
import {
  required,
  shouldNotExceedCharLength,
  shouldNotLessThanCharLength,
  validDateFormat,
  validEmailFormat,
  validPhoneNumberFormat,
  endDateShouldBeAfterStartDate,
} from '@ohrm/core/util/validation/rules';
import {OxdSwitchInput} from '@ohrm/oxd';
import useServerValidation from '@/core/util/composable/useServerValidation';
import useDateFormat from '@/core/util/composable/useDateFormat';

const DRAFT_KEY = 'aqe.pim.addEmployee.draft';
const defaultPic = `${window.appGlobal.publicPath}/images/default-photo.png`;

// Template (not a render fn) so the real oxd-button/oxd-form-actions -- and
// their scoped styles -- are used. The app bundles Vue's runtime compiler.
const WizardActions = {
  name: 'WizardActions',
  props: {step: Number, count: Number},
  emits: ['back', 'cancel'],
  template: `
    <div class="aqe-wiz__actions">
      <oxd-form-actions>
        <required-text />
        <oxd-button display-type="ghost" :label="$t('general.cancel')" @click="$emit('cancel')" />
        <oxd-button v-if="step > 0" display-type="ghost" label="Back" @click="$emit('back')" />
        <oxd-button display-type="secondary" label="Continue" icon-right-name="arrow-right" type="submit" />
      </oxd-form-actions>
    </div>`,
};

export default {
  components: {
    'oxd-switch-input': OxdSwitchInput,
    'full-name-input': FullNameInput,
    'password-input': PasswordInput,
    'jobtitle-dropdown': JobtitleDropdown,
    'subunit-dropdown': SubunitDropdown,
    'employment-status-dropdown': EmploymentStatusDropdown,
    'confirmation-dialog': ConfirmationDialog,
    'wizard-actions': WizardActions,
  },

  props: {
    empId: {type: String, required: true},
    allowedImageTypes: {type: Array, required: true},
    isPasswordRequired: {type: Boolean, default: true},
  },

  setup(props) {
    const employee = ref({
      firstName: '',
      middleName: '',
      lastName: '',
      empPicture: null,
      employeeId: props.empId ? props.empId : '',
    });
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/employees',
    );
    const {createUniqueValidator} = useServerValidation(http);
    const {userDateFormat} = useDateFormat();
    return {
      http,
      employee,
      userDateFormat,
      employeeIdUniqueValidation: createUniqueValidator(
        'Employee',
        'employeeId',
        {
          translateKey: 'pim.employee_id_exists',
        },
      ),
      usernameUniqueValidation: createUniqueValidator('User', 'userName', {
        matchByField: 'deleted',
        matchByValue: 'false',
        translateKey: 'pim.username_already_exists',
      }),
    };
  },

  data() {
    const yr = new Date().getFullYear();
    return {
      step: 0,
      furthest: 0,
      visited: [true, false, false, false],
      showIssues: false,
      isLoading: false,
      createLogin: false,
      dragOver: false,
      photoError: null,
      photoProgress: null,
      savedAt: null,
      draftOffer: null,
      saving: false,
      createdEmpNumber: null,
      personal: {
        birthday: null,
        gender: null,
        maritalStatus: null,
        nationality: null,
      },
      contact: {
        workEmail: '',
        mobile: '',
        country: null,
        state: null,
        city: null,
      },
      job: {
        joinedDate: null,
        jobTitle: null,
        empStatus: null,
        subunit: null,
        contractStart: null,
        contractEnd: null,
      },
      user: {
        username: '',
        userRoleId: 2,
        status: '1',
        password: '',
        passwordConfirm: '',
      },
      nationalities: [],
      countries: [],
      countryCodes: {},
      states: [],
      cities: [],
      geoLoading: null,
      birthYears: Array.from({length: 70}, (_, i) => yr - 16 - i),
      genders: [
        {id: 1, label: this.$t('general.male')},
        {id: 2, label: this.$t('general.female')},
      ],
      maritalOptions: [
        {id: 'Single', label: this.$t('pim.single')},
        {id: 'Married', label: this.$t('pim.married')},
        {id: 'Other', label: this.$t('pim.other')},
      ],
      steps: [
        {
          key: 'basics',
          title: 'Basics',
          hint: 'Who is joining? Name, employee ID and photo.',
        },
        {
          key: 'personal',
          title: 'Personal & contact',
          hint: 'How we reach them and where they are based.',
        },
        {key: 'job', title: 'Job', hint: 'Role, status and start date.'},
        {
          key: 'review',
          title: 'Account & review',
          hint: 'Optional login, then check everything before saving.',
        },
      ],
    };
  },

  computed: {
    rules() {
      return {
        firstName: [required, shouldNotExceedCharLength(30)],
        middleName: [shouldNotExceedCharLength(30)],
        lastName: [required, shouldNotExceedCharLength(30)],
        employeeId: [
          this.employeeIdUniqueValidation,
          shouldNotExceedCharLength(10),
        ],
        birthday: [
          validDateFormat(this.userDateFormat),
          (v) =>
            !v ||
            new Date(v) <= new Date(new Date().getFullYear() - 16, 11, 31) ||
            'Employee must be at least 16 years old',
        ],
        workEmail: [validEmailFormat, shouldNotExceedCharLength(50)],
        mobile: [validPhoneNumberFormat, shouldNotExceedCharLength(25)],
        joinedDate: [required, validDateFormat(this.userDateFormat)],
        jobTitle: [required],
        empStatus: [required],
        contractStart: [required, validDateFormat(this.userDateFormat)],
        contractEnd: [
          required,
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.job.contractStart,
            this.$t('general.end_date_should_be_after_start_date'),
          ),
        ],
        username: [
          required,
          this.usernameUniqueValidation,
          shouldNotLessThanCharLength(5),
          shouldNotExceedCharLength(40),
        ],
      };
    },
    needsContract() {
      return /contract|freelance|intern/i.test(this.job.empStatus?.label || '');
    },
    missing() {
      const m = {basics: [], personal: [], job: [], review: []};
      if (!this.employee.firstName.trim()) m.basics.push('First name');
      if (!this.employee.lastName.trim()) m.basics.push('Last name');
      if (!this.job.joinedDate) m.job.push('Joined date');
      if (!this.job.jobTitle) m.job.push('Job title');
      if (!this.job.empStatus) m.job.push('Employment status');
      if (this.needsContract && !this.job.contractStart)
        m.job.push('Contract start');
      if (this.needsContract && !this.job.contractEnd)
        m.job.push('Contract end');
      return m;
    },
    summary() {
      const full = [
        this.employee.firstName,
        this.employee.middleName,
        this.employee.lastName,
      ]
        .filter(Boolean)
        .join(' ');
      const place = [
        this.contact.city?.label,
        this.contact.state?.label,
        this.contact.country?.label,
      ]
        .filter(Boolean)
        .join(', ');
      return {
        basics: [
          {
            label: 'Name',
            value: full,
            missing: !this.employee.firstName || !this.employee.lastName,
          },
          {label: 'Employee ID', value: this.employee.employeeId},
          {
            label: 'Photo',
            value: this.employee.empPicture
              ? this.employee.empPicture.name
              : '',
          },
        ],
        personal: [
          {label: 'Date of birth', value: this.personal.birthday},
          {
            label: 'Gender',
            value: this.genders.find((g) => g.id === this.personal.gender)
              ?.label,
          },
          {label: 'Nationality', value: this.personal.nationality?.label},
          {label: 'Work email', value: this.contact.workEmail},
          {label: 'Mobile', value: this.contact.mobile},
          {label: 'Location', value: place},
        ],
        job: [
          {
            label: 'Joined',
            value: this.job.joinedDate,
            missing: !this.job.joinedDate,
          },
          {
            label: 'Job title',
            value: this.job.jobTitle?.label,
            missing: !this.job.jobTitle,
          },
          {
            label: 'Status',
            value: this.job.empStatus?.label,
            missing: !this.job.empStatus,
          },
          {label: 'Sub unit', value: this.job.subunit?.label},
          ...(this.needsContract
            ? [
                {
                  label: 'Contract',
                  value:
                    this.job.contractStart && this.job.contractEnd
                      ? `${this.job.contractStart} → ${this.job.contractEnd}`
                      : '',
                  missing: !this.job.contractStart || !this.job.contractEnd,
                },
              ]
            : []),
        ],
      };
    },
    profilePicUrl() {
      const p = this.employee.empPicture;
      return p ? `data:${p.type};base64,${p.base64}` : defaultPic;
    },
    dirty() {
      return !!(
        this.employee.firstName ||
        this.employee.lastName ||
        this.employee.empPicture ||
        this.personal.birthday ||
        this.contact.workEmail ||
        this.contact.mobile ||
        this.job.joinedDate ||
        this.job.jobTitle ||
        this.user.username
      );
    },
    draftable() {
      return {
        employee: {...this.employee, empPicture: null},
        personal: this.personal,
        contact: this.contact,
        job: this.job,
        createLogin: this.createLogin,
        user: {...this.user, password: '', passwordConfirm: ''},
        step: this.step,
        furthest: this.furthest,
      };
    },
  },

  watch: {
    draftable: {
      deep: true,
      handler() {
        if (this.saving || !this.dirty) return;
        clearTimeout(this._draftT);
        this._draftT = setTimeout(this.saveDraft, 900);
      },
    },
    'job.joinedDate'(d) {
      if (d && !this.job.contractStart) this.job.contractStart = d;
    },
    'contact.country'(c, old) {
      if (old !== undefined && c?.id !== old?.id) {
        this.contact.state = null;
        this.contact.city = null;
      }
      this.states = [];
      this.cities = [];
      if (c) this.loadGeo('state', {country: c.label});
    },
    'contact.state'(s, old) {
      if (old !== undefined && s?.id !== old?.id) this.contact.city = null;
      this.cities = [];
      if (s)
        this.loadGeo('city', {
          country: this.contact.country.label,
          state: s.label,
        });
    },
  },

  created() {
    this.loadDraftOffer();
    this.http
      .request({
        method: 'GET',
        url: '/api/v2/admin/nationalities',
        params: {limit: 0},
      })
      .then(({data}) => {
        this.nationalities = (data.data || []).map((n) => ({
          id: n.id,
          label: n.name,
        }));
      })
      .catch(() => {
        /* nationality list stays empty; the field is optional */
      });
    this.loadGeo('country');
  },

  mounted() {
    window.addEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    clearTimeout(this._draftT);
  },

  methods: {
    stepMeta(s, i) {
      if (this.showIssues && this.missing[s.key].length)
        return `${this.missing[s.key].length} missing`;
      if (i === this.step) return 'In progress';
      if (this.visited[i]) return 'Complete';
      return 'Not started';
    },
    kb(n) {
      return n > 1024 * 1024
        ? (n / 1048576).toFixed(1) + ' MB'
        : Math.ceil(n / 1024) + ' KB';
    },
    maskPhone(v) {
      // +CC NNNNN NNNNN (Indian / international style); keeps a leading +
      let d = String(v || '').replace(/[^\d+]/g, '');
      const plus = d.startsWith('+');
      d = d.replace(/\+/g, '').slice(0, 13);
      if (!plus && d.length <= 10)
        return d.replace(/^(\d{5})(\d{1,5})$/, '$1 $2');
      const cc = d.slice(0, d.length > 10 ? d.length - 10 : 2);
      const rest = d.slice(cc.length);
      return (
        '+' +
        cc +
        (rest ? ' ' + rest.replace(/^(\d{5})(\d{0,5})$/, '$1 $2') : '')
      ).trim();
    },
    async loadGeo(level, params = {}) {
      this.geoLoading = level;
      try {
        const {data} = await this.http.request({
          method: 'GET',
          url: '/core/data/geo',
          baseURL: window.appGlobal.baseUrl,
          params,
        });
        const items = (data.items || []).map((label, i) => ({
          id: i + 1,
          label,
        }));
        if (level === 'country') {
          this.countries = items;
          this.countryCodes = data.codes || {};
        } else if (level === 'state') this.states = items;
        else this.cities = items;
      } catch (e) {
        /* leave the list empty; the field stays usable */
      } finally {
        this.geoLoading = null;
      }
    },

    // ── navigation ──
    async validateCurrent() {
      const form = this.$refs.stepForm;
      if (!form) return true;
      await form.validate();
      return !form.isFromInvalid;
    },
    async next() {
      if (this.step === 0 && this.photoError) return;
      this.step = Math.min(this.step + 1, this.steps.length - 1);
      this.visited[this.step] = true;
      this.furthest = Math.max(this.furthest, this.step);
      this.scrollTop();
    },
    back() {
      this.step = Math.max(0, this.step - 1);
      this.scrollTop();
    },
    async goTo(i) {
      if (i === this.step) return;
      if (i > this.step && !(await this.validateCurrent())) return;
      this.step = i;
      this.visited[i] = true;
      this.furthest = Math.max(this.furthest, i);
      this.scrollTop();
    },
    scrollTop() {
      this.$nextTick(() =>
        this.$el.scrollIntoView({behavior: 'smooth', block: 'start'}),
      );
    },

    // ── photo ──
    onPick(e) {
      const f = e.target.files && e.target.files[0];
      e.target.value = '';
      if (f) this.readPhoto(f);
    },
    onDrop(e) {
      this.dragOver = false;
      const f = e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) this.readPhoto(f);
    },
    readPhoto(file) {
      this.photoError = null;
      if (!this.allowedImageTypes.includes(file.type)) {
        this.photoError = this.$t('general.file_type_not_allowed');
        return;
      }
      if (file.size > 1024 * 1024) {
        this.photoError = this.$t('general.attachment_size_exceeded');
        return;
      }
      const reader = new FileReader();
      this.photoProgress = 0;
      reader.onprogress = (ev) => {
        if (ev.lengthComputable)
          this.photoProgress = Math.round((ev.loaded / ev.total) * 100);
      };
      reader.onload = () => {
        const base64 = String(reader.result).split(',')[1];
        this.employee.empPicture = {
          name: file.name,
          type: file.type,
          size: file.size,
          base64,
        };
        setTimeout(() => (this.photoProgress = null), 300);
      };
      reader.onerror = () => {
        this.photoError = 'Could not read that file';
        this.photoProgress = null;
      };
      reader.readAsDataURL(file);
    },

    // ── drafts / leaving ──
    saveDraft() {
      try {
        localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({at: Date.now(), data: this.draftable}),
        );
        this.savedAt = new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      } catch (e) {
        /* storage full or unavailable */
      }
    },
    loadDraftOffer() {
      try {
        const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
        if (!d || !d.data) return;
        const e = d.data.employee || {};
        this.draftOffer = {
          when: new Date(d.at).toLocaleString([], {
            dateStyle: 'medium',
            timeStyle: 'short',
          }),
          name: [e.firstName, e.lastName].filter(Boolean).join(' '),
          data: d.data,
        };
      } catch (e) {
        /* corrupt draft: ignore */
      }
    },
    restoreDraft() {
      const d = this.draftOffer.data;
      this.employee = {...this.employee, ...d.employee, empPicture: null};
      Object.assign(this.personal, d.personal);
      Object.assign(this.contact, d.contact);
      Object.assign(this.job, d.job);
      Object.assign(this.user, d.user);
      this.createLogin = !!d.createLogin;
      this.furthest = d.furthest || 0;
      this.visited = this.visited.map((_, i) => i <= this.furthest);
      this.step = Math.min(d.step || 0, this.steps.length - 1);
      this.draftOffer = null;
    },
    discardDraft() {
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch (e) {
        /* ignore */
      }
      this.draftOffer = null;
    },
    onBeforeUnload(e) {
      if (this.dirty && !this.saving) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    async onCancel() {
      if (this.dirty) {
        const r = await this.$refs.leaveDialog.showDialog();
        if (r !== 'ok') return;
      }
      this.saving = true; // suppress the beforeunload prompt
      navigate('/pim/viewEmployeeList');
    },

    // ── save ──
    async onSave() {
      this.showIssues = true;
      const firstBad = this.steps.findIndex((s) => this.missing[s.key].length);
      if (firstBad > -1) {
        this.$toast.warn({
          title: this.$t('general.warning'),
          message: `Complete the ${this.steps[firstBad].title} step first`,
        });
        this.step = firstBad;
        return;
      }
      this.isLoading = true;
      this.saving = true;
      try {
        // Retry-safe: if an earlier attempt created the employee but a later
        // call failed, continue from the existing record instead of creating
        // a duplicate (which the unique employee-ID rule would reject).
        if (!this.createdEmpNumber) {
          const {data} = await this.http.create({...this.employee});
          this.createdEmpNumber = data?.data?.empNumber;
        }
        const empNumber = this.createdEmpNumber;
        const base = `/api/v2/pim/employees/${empNumber}`;
        // Sequential on purpose: these all write the same employee row.
        const p = this.personal;
        if (p.birthday || p.gender || p.maritalStatus || p.nationality) {
          await this.http.request({
            method: 'PUT',
            url: `${base}/personal-details`,
            data: {
              firstName: this.employee.firstName,
              middleName: this.employee.middleName,
              lastName: this.employee.lastName,
              employeeId: this.employee.employeeId,
              birthday: p.birthday,
              gender: p.gender,
              maritalStatus: p.maritalStatus?.id || '',
              nationalityId: p.nationality?.id ?? null,
            },
          });
        }
        const c = this.contact;
        if (c.workEmail || c.mobile || c.country) {
          await this.http.request({
            method: 'PUT',
            url: `/api/v2/pim/employee/${empNumber}/contact-details`,
            data: {
              workEmail: c.workEmail || null,
              mobile: c.mobile || null,
              countryCode: c.country
                ? this.countryCodes[c.country.label] || null
                : null,
              province: c.state?.label || null,
              city: c.city?.label || null,
            },
          });
        }
        await this.http.request({
          method: 'PUT',
          url: `${base}/job-details`,
          data: {
            joinedDate: this.job.joinedDate,
            jobTitleId: this.job.jobTitle?.id,
            empStatusId: this.job.empStatus?.id,
            subunitId: this.job.subunit?.id,
          },
        });
        if (this.needsContract) {
          // No currentContractAttachment: there is no existing file to keep.
          await this.http.request({
            method: 'PUT',
            url: `${base}/employment-contract`,
            data: {
              startDate: this.job.contractStart,
              endDate: this.job.contractEnd,
            },
          });
        }
        if (this.createLogin) {
          await this.http.request({
            method: 'POST',
            url: '/api/v2/admin/users',
            data: {
              username: this.user.username,
              password: this.user.password,
              status: this.user.status == '1',
              userRoleId: this.user.userRoleId,
              empNumber,
            },
          });
        }
        this.discardDraft();
        await this.$toast.saveSuccess();
        navigate(`/pim/viewPersonalDetails/empNumber/${empNumber}`);
      } catch (e) {
        this.saving = false;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style src="./employee.scss" lang="scss" scoped></style>

<style lang="scss" scoped>
$ink: #0f172a;
$ink2: #334155;
$ink3: #64748b;
$ink4: #94a3b8;
$line: rgba(15, 23, 42, 0.08);
$indigo: #6366f1;
$green: #059669;

.aqe-wiz__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.aqe-wiz__sub {
  margin: 4px 0 0;
  color: $ink3;
  font-size: 14px;
}
.aqe-wiz__saved {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: $ink4;
  font-size: 12.5px;
  white-space: nowrap;
}
.aqe-wiz__draft {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 16px 0 0;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(99, 102, 241, 0.07);
  color: #3730a3;
  font-size: 13.5px;
}
.aqe-wiz__link {
  border: 0;
  background: none;
  padding: 0;
  color: $indigo;
  font: 600 13px Inter, sans-serif;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  &.is-muted {
    color: $ink3;
  }
  &.is-danger {
    color: #dc2626;
  }
}
.aqe-wiz__steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin: 22px 0 0;
  padding: 0;
  list-style: none;
  li button {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    border: 0;
    border-radius: 14px;
    background: transparent;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s;
    &:hover:not(:disabled) {
      background: rgba(15, 23, 42, 0.04);
    }
    &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }
  }
  li.is-current button {
    background: rgba(15, 23, 42, 0.05);
  }
}
.aqe-wiz__dot {
  flex: 0 0 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #fff;
  box-shadow: inset 0 0 0 1.5px rgba(15, 23, 42, 0.15);
  color: $ink3;
  font: 700 13px Inter, sans-serif;
  .is-current & {
    background: $ink;
    box-shadow: none;
    color: #fff;
  }
  .is-done & {
    background: $green;
    box-shadow: none;
    color: #fff;
  }
  .is-error & {
    background: #dc2626;
    box-shadow: none;
    color: #fff;
  }
}
.aqe-wiz__stxt {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.aqe-wiz__sname {
  color: $ink;
  font: 600 13.5px Inter, sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.aqe-wiz__smeta {
  color: $ink4;
  font-size: 12px;
  .is-error & {
    color: #dc2626;
  }
}
.aqe-wiz__progress {
  height: 3px;
  margin: 12px 0 22px;
  border-radius: 3px;
  background: rgba(15, 23, 42, 0.06);
  overflow: hidden;
  span {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: linear-gradient(90deg, #10b981, $indigo);
    transition: width 0.35s ease;
  }
}
.aqe-wiz__actions {
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid $line;
}

/* photo dropzone */
.aqe-photo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 220px;
  margin-right: 24px;
}
.aqe-photo__drop {
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 16px;
  border-radius: 24px;
  border: 1.5px dashed rgba(15, 23, 42, 0.18);
  background: #f8fafc;
  color: $ink3;
  font-size: 13px;
  text-align: center;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.15s, background-color 0.15s;
  i {
    font-size: 28px;
    color: $indigo;
  }
  strong {
    color: $ink;
  }
  small {
    color: $ink4;
  }
  &:hover,
  &:focus-visible,
  &.is-over {
    outline: none;
    border-color: $indigo;
    background: rgba(99, 102, 241, 0.05);
  }
  &.has-img {
    padding: 0;
    border-style: solid;
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
.aqe-photo__progress {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 14px;
  height: 4px;
  border-radius: 4px;
  background: rgba(15, 23, 42, 0.1);
  span {
    display: block;
    height: 100%;
    border-radius: 4px;
    background: $indigo;
    transition: width 0.1s;
  }
}
.aqe-photo__input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
.aqe-photo__row {
  display: flex;
  gap: 10px;
  align-items: center;
  font-size: 12.5px;
  color: $ink3;
}
.aqe-photo__name {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.aqe-wiz__err {
  color: #dc2626;
  font-size: 12px;
}

/* segmented gender control */
.aqe-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.aqe-seg {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.05);
  width: max-content;
  button {
    height: 34px;
    padding: 0 18px;
    border: 0;
    border-radius: 9px;
    background: transparent;
    color: $ink2;
    font: 600 13px Inter, sans-serif;
    cursor: pointer;
    &[aria-checked='true'] {
      background: #fff;
      color: $ink;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12),
        0 0 0 1px rgba(15, 23, 42, 0.04);
    }
  }
}

/* conditional contract block */
.aqe-wiz__callout {
  margin: 6px 0 14px;
  padding: 16px 18px 4px;
  border-radius: 16px;
  background: rgba(245, 158, 11, 0.07);
  box-shadow: inset 0 0 0 1px rgba(245, 158, 11, 0.25);
}
.aqe-wiz__callout-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #92400e;
  font: 600 13.5px Inter, sans-serif;
}
.aqe-wiz-reveal-enter-active,
.aqe-wiz-reveal-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.aqe-wiz-reveal-enter-from,
.aqe-wiz-reveal-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* review */
.aqe-review {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
}
.aqe-review__card {
  padding: 16px 18px;
  border-radius: 16px;
  background: #f8fafc;
  box-shadow: inset 0 0 0 1px $line;
  header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    color: $ink;
    font: 700 14px Inter, sans-serif;
  }
  dl {
    display: grid;
    gap: 8px;
    margin: 0;
    div {
      display: flex;
      justify-content: space-between;
      gap: 12px;
    }
  }
  dt {
    color: $ink3;
    font-size: 13px;
  }
  dd {
    margin: 0;
    color: $ink;
    font-size: 13px;
    font-weight: 600;
    text-align: right;
    overflow-wrap: anywhere;
    &.is-missing {
      color: #dc2626;
    }
  }
}
.aqe-review__warn {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin: 12px 0 0;
  color: #b91c1c;
  font-size: 12.5px;
}

@media (max-width: 800px) {
  .aqe-wiz__steps {
    grid-template-columns: repeat(4, auto);
    overflow-x: auto;
  }
  .aqe-wiz__stxt {
    display: none;
  }
  .aqe-photo {
    width: 100%;
    margin: 0 0 16px;
  }
}
</style>
